import React from "react";
import { Link } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const FeaturedPostHero = () => {
  // Obtener el primer post destacado para usar como hero
  const {
    data: posts,
    loading,
    error,
  } = useFetch(apiEndpoints.posts.pinned(1));

  const featuredPost = posts?.[0];

  if (loading) {
    return (
      <section className="featured-post-hero">
        <div className="container">
          <div className="hero-loading">
            <div className="loading-spinner">
              ⏳ Cargando noticia destacada...
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !featuredPost) {
    return null; // No mostrar nada si hay error o no hay posts
  }

  return (
    <section className="featured-post-hero">
      <style jsx>{`
        .featured-post-hero {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
          padding: 80px 0;
          position: relative;
          overflow: hidden;
          width: 100vw;
          margin-left: calc(-50vw + 50%);
        }

        .featured-post-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.03)"/><circle cx="20" cy="80" r="0.5" fill="rgba(255,255,255,0.03)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          pointer-events: none;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
          position: relative;
          z-index: 1;
        }

        .hero-content {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hero-text {
          color: white;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 20px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .hero-title {
          font-size: 3rem;
          font-weight: bold;
          line-height: 1.1;
          margin-bottom: 20px;
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-excerpt {
          font-size: 1.2rem;
          color: #cbd5e1;
          line-height: 1.6;
          margin-bottom: 30px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .hero-meta {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 30px;
          font-size: 0.9rem;
          color: #94a3b8;
        }

        .hero-date {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .hero-author {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .hero-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
        }

        .hero-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 40px rgba(59, 130, 246, 0.3);
          color: white;
        }

        .hero-image-container {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
          transform: rotate(2deg);
          transition: transform 0.3s ease;
        }

        .hero-image-container:hover {
          transform: rotate(0deg) scale(1.02);
        }

        .hero-image {
          width: 100%;
          height: 400px;
          object-fit: cover;
          display: block;
        }

        .hero-image-placeholder {
          width: 100%;
          height: 400px;
          background: linear-gradient(135deg, #374151, #4b5563);
          display: none;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 4rem;
        }

        .hero-loading {
          text-align: center;
          padding: 60px 20px;
          color: white;
        }

        .loading-spinner {
          font-size: 1.5rem;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .hero-content {
            grid-template-columns: 1fr;
            gap: 40px;
            text-align: center;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .hero-image-container {
            transform: none;
            max-width: 500px;
            margin: 0 auto;
          }

          .hero-image-container:hover {
            transform: scale(1.02);
          }
        }

        @media (max-width: 768px) {
          .featured-post-hero {
            padding: 60px 0;
          }

          .hero-title {
            font-size: 2rem;
          }

          .hero-excerpt {
            font-size: 1.1rem;
          }

          .hero-meta {
            justify-content: center;
            flex-wrap: wrap;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 1.8rem;
          }

          .hero-excerpt {
            font-size: 1rem;
          }

          .hero-image {
            height: 300px;
          }

          .hero-image-placeholder {
            height: 300px;
          }
        }
      `}</style>

      <div className="container">
        <div className="hero-content">
          {/* Contenido de texto */}
          <div className="hero-text">
            <div className="hero-badge">
              <span>⭐</span>
              <span>Noticia Destacada</span>
            </div>

            <h1 className="hero-title">{featuredPost.title}</h1>

            {(featuredPost.excerpt || featuredPost.description) && (
              <p className="hero-excerpt">
                {featuredPost.excerpt || featuredPost.description}
              </p>
            )}

            <div className="hero-meta">
              <div className="hero-date">
                <span>📅</span>
                <span>
                  {new Date(
                    featuredPost.published_at || featuredPost.created_at
                  ).toLocaleDateString("es-ES", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>

              {featuredPost.author && (
                <div className="hero-author">
                  <span>✍️</span>
                  <span>Por {featuredPost.author}</span>
                </div>
              )}
            </div>

            <Link to={`/post/${featuredPost.slug}`} className="hero-cta">
              <span>Leer noticia completa</span>
              <span>→</span>
            </Link>
          </div>

          {/* Imagen destacada */}
          <div className="hero-image-container">
            {featuredPost.image ? (
              <img
                src={`${API_BASE_URL}/uploads/${featuredPost.image}`}
                alt={featuredPost.title}
                className="hero-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}

            <div className="hero-image-placeholder">📰</div>
          </div>
        </div>
      </div>
    </section>
  );
};
